import React from 'react'
import type { BatchStatus } from './StatusBadge'

interface ProgressBarProps {
  progress: number
  status: BatchStatus
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  status,
}) => {
  const clamped = Math.max(0, Math.min(100, Math.round(progress)))

  const isComplete = status === 'completed' || clamped === 100

  let barClass =
    'h-full rounded-full transition-all duration-700 ease-in-out relative overflow-hidden bg-gradient-to-r '

  if (isComplete && clamped >= 100) {
    barClass +=
      'from-emerald-500 to-green-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]'
  } else if (isComplete && clamped < 100) {
    barClass += 'from-amber-500 to-orange-500'
  } else if (status === 'processing' || status === 'queued') {
    barClass += 'from-blue-600 via-indigo-600 to-purple-600'
  } else if (status === 'failed') {
    barClass += 'from-red-500 to-rose-600'
  } else {
    barClass += 'from-slate-400 to-slate-500'
  }

  return (
    <div className='w-full flex items-center gap-3 min-w-[140px]'>
      <div className='flex-1 min-w-[80px] h-2.5 bg-slate-200 rounded-full overflow-hidden border border-slate-300/30 shadow-inner'>
        <div className={barClass} style={{ width: `${clamped}%` }}>
          {(status === 'processing' || status === 'queued') && (
            <div className='absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full' />
          )}
        </div>
      </div>
      <span className='text-xs text-slate-600 font-bold shrink-0 tabular-nums'>
        {clamped}%
      </span>
      <style>{`
        @keyframes progress-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: progress-shimmer 2s infinite linear;
        }
      `}</style>
    </div>
  )
}

export default ProgressBar
